import React, { useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { onUpdateAvailable, applyUpdate, getCurrentVersion } from "@/lib/swUpdater";

const BUSY_SELECTORS = [
  "[data-testid='lawyer-lcr-page']",
  "[data-testid='consultation-modal']",
  "[data-testid='booking-query']",
];

const isBusyWorkflow = () => BUSY_SELECTORS.some((s) => document.querySelector(s));

export default function UpdateBanner() {
  const [waiting, setWaiting] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [updateType, setUpdateType] = useState("optional");
  const [version, setVersion] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const off = onUpdateAvailable((w) => {
      setWaiting(w);
      api.get("/app/update-policy").then((r) => setUpdateType(r.data?.update_type || "optional")).catch(() => {});
      getCurrentVersion().then(setVersion);
    });
    return off;
  }, []);

  if (!waiting || (dismissed && updateType !== "mandatory")) return null;

  const doUpdate = () => {
    setApplying(true);
    applyUpdate(waiting);
    // Never interrupt an active workflow automatically — the reload only
    // happens here, after the user's explicit click.
  };

  if (updateType === "mandatory") {
    return (
      <div className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-6" data-testid="update-mandatory-overlay">
        <div className="max-w-sm w-full bg-white rounded-3xl p-7 shadow-2xl text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-[#FBF4DF] grid place-items-center text-[#8a6d14] mb-4">
            <RefreshCw size={22} className={applying ? "animate-spin" : ""} />
          </div>
          <h2 className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            Advoset needs to be updated
          </h2>
          <p className="mt-2 text-sm text-slate-500">A new version is required to continue using Advoset.</p>
          <button
            onClick={doUpdate}
            disabled={applying}
            className="mt-5 w-full h-11 rounded-xl bg-[#0A2540] text-[#D4AF37] font-bold text-sm hover:opacity-90 disabled:opacity-60"
            data-testid="update-continue-btn"
          >
            {applying ? "Updating…" : "Update & Continue"}
          </button>
          {version && <p className="mt-3 text-[10px] text-slate-400">current build {version}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[95] w-[92%] max-w-md" data-testid="update-banner">
      <div className="rounded-2xl bg-[#0A2540] text-white shadow-2xl p-4 flex items-start gap-3 border border-white/10">
        <div className="h-9 w-9 shrink-0 rounded-xl bg-white/10 grid place-items-center text-[#D4AF37]">
          <Sparkles size={17} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold">New version available</p>
          <p className="text-xs text-white/70 mt-0.5">
            We've improved Advoset with the latest updates and fixes.
            {isBusyWorkflow() && " Finish what you're doing first — updating reloads the app."}
          </p>
          <div className="mt-2.5 flex gap-2">
            <button
              onClick={doUpdate}
              disabled={applying}
              className="px-4 h-8 rounded-full bg-[#D4AF37] text-[#0A2540] text-xs font-extrabold hover:opacity-90 disabled:opacity-60"
              data-testid="update-now-btn"
            >
              {applying ? "Updating…" : "Update now"}
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="px-4 h-8 rounded-full border border-white/20 text-xs font-bold text-white/80 hover:bg-white/10"
              data-testid="update-later-btn"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
